import type { QueryRankingElementRankingDTO } from '@empathyco/playboard-core-domain'
import type { Ref, SetupContext } from 'vue'
import { nextTick, ref } from 'vue'

export interface QueryRankingModalComposable<NestedModalAcceptEventPayload> {
  modal: Ref<QueryRankingModalComposable<NestedModalAcceptEventPayload> | null>
  open: () => Promise<void>
  accept: (acceptEventPayload: NestedModalAcceptEventPayload) => void
  close: () => void
  onClose: () => void
  schedule: Ref<QueryRankingElementRankingDTO['schedule']>
}

export interface QueryRankingModalComposableProps<
  ThisModalAcceptEventPayload,
  NestedModalAcceptEventPayload,
> {
  modal?: Ref<QueryRankingModalComposable<NestedModalAcceptEventPayload> | null>
  resetFields: () => void
  buildAcceptEventPayload: (
    acceptEventPayload: NestedModalAcceptEventPayload,
  ) => ThisModalAcceptEventPayload
}

const schedule = ref({
  activationDate: undefined,
  deactivationDate: undefined,
})

export type AcceptEventPayload = Pick<QueryRankingElementRankingDTO, 'action' | 'schedule'>

export function useQueryRankingModal<ThisModalAcceptEventPayload, NestedModalAcceptEventPayload>(
  {
    modal = ref(null),
    resetFields,
    buildAcceptEventPayload,
  }: QueryRankingModalComposableProps<ThisModalAcceptEventPayload, NestedModalAcceptEventPayload>,
  { emit }: Pick<SetupContext, 'emit'>,
): QueryRankingModalComposable<NestedModalAcceptEventPayload> {
  async function open(): Promise<void> {
    await nextTick()
    resetFields()
    modal.value?.open()
  }

  function accept(acceptEventPayload: NestedModalAcceptEventPayload): void {
    emit('accept', buildAcceptEventPayload(acceptEventPayload))
    close()
  }

  function close(): void {
    modal.value?.close()
  }

  function onClose(): void {
    emit('close')
  }

  return {
    accept,
    close,
    modal,
    onClose,
    open,
    schedule,
  }
}
